import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, FileText, Receipt, Contact2, Wrench, BarChart3, LogOut, Menu, X, Sparkles, MessageSquare, Calendar as CalendarIcon, Package, Settings as SettingsIcon, Clock, Archive, CreditCard, Crown, Terminal, Gauge,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBusinessProfile } from "../hooks/useBusinessProfile";
import { useTenant, resolveLogoUrl } from "../context/TenantContext";
import { useUnviewedLeads } from "../hooks/useUnviewedLeads";
import { useUnquotedQuotes } from "../hooks/useUnquotedQuotes";
import { useOverdueJobs } from "../hooks/useOverdueJobs";
import { cn } from "../lib/utils";
import BillingBanner from "./BillingBanner";
import ImpersonationBanner from "./ImpersonationBanner";

const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon, testId: "nav-calendar" },
  { to: "/leads", label: "Leads", icon: Users, testId: "nav-leads", perm: "view_leads" },
  { to: "/quotes", label: "Quotes", icon: FileText, testId: "nav-quotes", perm: "view_quotes" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices", perm: "view_invoices" },
  { to: "/contacts", label: "Contacts", icon: Contact2, testId: "nav-contacts" },
  { to: "/jobs", label: "Jobs", icon: Wrench, testId: "nav-jobs" },
  { to: "/inventory", label: "Inventory", icon: Package, testId: "nav-inventory", perm: "manage_inventory" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics", perm: "view_analytics" },
  { to: "/archive", label: "Archive", icon: Archive, testId: "nav-archive" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

// Office = front-desk staff. Shares the same nav list as admin; the
// per-item `perm` gates filter Analytics out for offices that don't
// have view_analytics turned on. Admins that toggle view_analytics=true
// on the office role (or a custom office-based role) get the tab back.
const OFFICE_NAV = ADMIN_NAV;

const INSTALLER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon, testId: "nav-calendar" },
  { to: "/jobs", label: "My Jobs", icon: Wrench, testId: "nav-jobs" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics", perm: "view_analytics" },
];

// Platform-admin-only nav. The master account (platform@wrapconsole.com)
// doesn't run a shop so it never needs Leads/Quotes/Jobs/Inventory etc.
// It only ever manages the overall platform — monitoring every tenant,
// running cross-shop commands, inspecting fleet-wide analytics, and
// setting platform-wide defaults. This nav replaces ADMIN_NAV entirely
// when the user has `is_platform_admin: true`.
const PLATFORM_NAV = [
  { to: "/platform/overview",  label: "Overview",  icon: Gauge,        testId: "nav-platform-overview" },
  { to: "/platform/analytics", label: "Analytics", icon: BarChart3,    testId: "nav-platform-analytics" },
  { to: "/platform/commands",  label: "Commands",  icon: Terminal,     testId: "nav-platform-commands" },
  { to: "/platform/settings",  label: "Settings",  icon: SettingsIcon, testId: "nav-platform-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { profile } = useBusinessProfile();
  const { tenant } = useTenant();
  const { count: unviewedLeads } = useUnviewedLeads();
  const { count: unquotedQuotes } = useUnquotedQuotes();
  const { count: overdueJobs } = useOverdueJobs();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Filter nav items by the user's effective permission map. Items without
  // a `perm` field are always visible (like Dashboard / Calendar).
  // A custom role picks the base nav of the built-in it inherits from
  // (base_role), so a "Manager" custom role based on office still starts
  // from OFFICE_NAV rather than the sparser INSTALLER_NAV.
  const perms = user?.permissions || {};
  const isAdmin = user?.role === "admin";
  const effectiveRole = user?.base_role || user?.role;
  const baseItems = isAdmin
    ? ADMIN_NAV
    : effectiveRole === "office"
      ? OFFICE_NAV
      : effectiveRole === "admin"
        ? ADMIN_NAV
        : INSTALLER_NAV;
  const isPlatform = !!user?.is_platform_admin && !user?.is_impersonating;
  const items = isPlatform ? PLATFORM_NAV : baseItems.filter((n) => !n.perm || isAdmin || !!perms[n.perm]);
  const shopName = isPlatform ? "WrapConsole" : (tenant?.name || profile?.name || "WrapConsole");
  const shopLogo = isPlatform ? "" : resolveLogoUrl(tenant?.logo_url || "");

  const badgeFor = (to) => {
    if (to === "/leads" && unviewedLeads > 0) return { count: unviewedLeads, tone: "red" };
    if (to === "/quotes" && unquotedQuotes > 0) return { count: unquotedQuotes, tone: "red" };
    if (to === "/jobs" && overdueJobs > 0) return { count: overdueJobs, tone: "amber" };
    return null;
  };
  const badgeTitleFor = (to, n) => {
    if (to === "/leads") return `${n} new lead${n === 1 ? "" : "s"}`;
    if (to === "/quotes") return `${n} unquoted enquir${n === 1 ? "y" : "ies"}`;
    if (to === "/jobs") return `${n} overdue booking${n === 1 ? "" : "s"} still unbooked`;
    return "";
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen grain relative">
      {/* Sidebar (desktop) */}
      <aside data-testid="sidebar" className="hidden md:flex fixed left-4 top-4 bottom-4 w-64 glass rounded-3xl flex-col p-5 z-40">
        <div className="flex items-center gap-2 mb-10 px-2">
          {shopLogo ? (
            <img
              data-testid="shop-logo"
              src={shopLogo}
              alt={shopName}
              className="w-9 h-9 rounded-xl object-cover border border-white/10 bg-white/5"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500/40 to-emerald-500/30 border border-white/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
          )}
          <div>
            <div data-testid="shop-name" className="font-heading text-lg tracking-tight text-white leading-none truncate max-w-[10rem]" title={shopName}>{shopName}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">
              {isPlatform ? "Platform" : user?.is_impersonating ? "Read-only" : user?.role === "admin" ? "Master" : user?.role === "office" ? "Office" : user?.role === "assistant" ? "Assistant" : "Installer"}
            </div>
          </div>
        </div>

        <nav className="flex-1 flex flex-col gap-1">
          {items.map((item) => {
            const badge = badgeFor(item.to);
            return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-medium transition-all",
                  isActive
                    ? "bg-white/[0.08] text-white border border-white/10"
                    : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                )
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
              {badge && (
                <span
                  data-testid={`nav-badge-${item.to.replace("/", "") || "home"}`}
                  className={cn(
                    "ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold animate-pulse",
                    badge.tone === "amber"
                      ? "bg-amber-500 shadow-[0_0_0_2px_rgba(245,158,11,0.25)]"
                      : "bg-red-500 shadow-[0_0_0_2px_rgba(239,68,68,0.25)]"
                  )}
                  title={badgeTitleFor(item.to, badge.count)}
                >
                  {badge.count > 99 ? "99+" : badge.count}
                </span>
              )}
            </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto pt-4 border-t border-white/5">
          <div className="px-2 mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Signed in</div>
            <div className="text-sm text-white truncate">{user?.name || user?.email}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
          </div>
          {user?.role === "admin" && !user?.is_platform_admin && (
            <NavLink
              to="/billing"
              data-testid="nav-billing"
              className={({ isActive }) => cn(
                "w-full flex items-center gap-2 px-4 py-2 rounded-full text-xs mb-2 transition-all",
                isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5",
              )}
            >
              <CreditCard className="w-3.5 h-3.5" /> Plan &amp; billing
            </NavLink>
          )}
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10 hover:text-white transition-all"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 px-4 pt-4">
        <div className="glass rounded-2xl flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {shopLogo ? (
              <img src={shopLogo} alt={shopName} className="w-8 h-8 rounded-lg object-cover border border-white/10 bg-white/5" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/40 to-emerald-500/30 border border-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
            )}
            <span className="font-heading text-base text-white truncate max-w-[7rem]">{shopName}</span>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="p-2 rounded-full bg-white/5 border border-white/10 text-white"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
        {mobileOpen && (
          <div data-testid="mobile-menu" className="glass rounded-2xl mt-2 p-3 flex flex-col gap-1">
            {items.map((item) => {
              const badge = badgeFor(item.to);
              return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-${item.testId}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all",
                    isActive ? "bg-white/[0.08] text-white" : "text-slate-300 hover:bg-white/[0.04]"
                  )
                }
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
                {badge && (
                  <span className={cn(
                    "ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold",
                    badge.tone === "amber" ? "bg-amber-500" : "bg-red-500"
                  )}>
                    {badge.count > 99 ? "99+" : badge.count}
                  </span>
                )}
              </NavLink>
              );
            })}
            <button
              data-testid="mobile-logout-button"
              onClick={handleLogout}
              className="mt-2 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        )}
      </header>

      {/* Mobile bottom dock */}
      <nav className="md:hidden fixed bottom-3 left-3 right-3 z-40 glass-strong rounded-full px-2 py-2 flex items-center justify-around">
        {items.slice(0, 4).map((item) => {
          const badge = badgeFor(item.to);
          return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            data-testid={`bottomnav-${item.testId}`}
            className={({ isActive }) =>
              cn(
                "relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-full transition-all",
                isActive ? "bg-white/10 text-white" : "text-slate-400"
              )
            }
          >
            <item.icon className="w-4 h-4" />
            <span className="text-[10px]">{item.label}</span>
            {badge > 0 && (
              <span className="absolute top-0 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {badge > 9 ? "9+" : badge}
              </span>
            )}
          </NavLink>
          );
        })}
      </nav>

      {/* Main content — extra bottom padding on mobile so the fixed bottom
          nav (bottom-3 + h~68px + iOS safe area) never overlaps the last
          card. On desktop the sidebar handles nav so we can breathe. */}
      <main className="md:ml-72 px-4 md:px-8 lg:px-10 py-6 md:py-8 pb-32 md:pb-10 relative z-10">
        <ImpersonationBanner />
        {!isPlatform && <BillingBanner />}
        {children}
      </main>
    </div>
  );
}
